/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Modal } from 'Components/Modal'
import { LocationSelect } from 'Components/Cards/LocationCard/LocationSelect'
import { type location, type lookup } from 'core/types'
import { useEffect, useState } from 'react'
import { Box, Button, FormControlLabel, RadioGroup, Typography } from '@mui/material'
import Radio from '@mui/material/Radio'
import { toast } from 'react-toastify'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { SchoolIcon } from 'Assets/SVGs/school'
import { sendGet } from 'hooks/use-fetch'
import { SelectWithLabel } from 'Components/SelectWithLabel'

export interface ChangeAddressProps {
  open: boolean
  onClose: () => void
  onSubmit: (location: location, type: AddressChangeType, selectedMembers: Array<{ id: number, type: MemberType }>) => void
  title?: string
  subjectLabel?: string
  subjectId?: number
  hideSharedChoice?: boolean
  initialLocation?: location
  sharedChildren?: Array<{ id: number, name: string }>
  inline?: boolean
  showDistrict?: boolean
  // the child's ISD - its districts are offered by hand when the address is homeless or doesn't resolve to one
  isdID?: number
}

interface AddressDistrict {
  districtName: string
  isdName: string
}

export enum AddressChangeType {
  Household,
  SinglePerson,
  SelectMembers
}

export enum MemberType {
  Caregiver,
  Child
}

interface Member {
  id: number
  type: MemberType
  name: string
}

export function ChangeAddress (props: ChangeAddressProps): JSX.Element {
  const hideSharedChoice = props.hideSharedChoice ?? false
  const subjectLabel = props.subjectLabel ?? 'this child'
  const [newLocation, setNewLocation] = useState<location>({ ...(props.initialLocation ?? {} as location), isHomeless: props.initialLocation?.isHomeless ?? false })
  const isHomeless = newLocation.isHomeless ?? false
  const [district, setDistrict] = useState<AddressDistrict | null>(null)
  // null until the entered address has been looked up
  const [districtResolved, setDistrictResolved] = useState<boolean | null>(null)
  const [manualDistrictID, setManualDistrictID] = useState<number | undefined>(props.initialLocation?.districtID ?? undefined)
  const [districtOptions, setDistrictOptions] = useState<lookup[] | null>(null)
  const showManualDistrict = props.showDistrict === true && (props.isdID ?? 0) > 0 && (isHomeless || districtResolved === false)
  const [changeType, setChangeType] = useState<AddressChangeType>(AddressChangeType.SinglePerson)
  const [selectedMembers, setSelectedMembers] = useState<Array<{ id: number, type: MemberType }>>([])
  const [fieldErrors, setFieldErrors] = useState<string[]>([])

  const handleClose = (): void => {
    props.onClose()
  }

  const locationSelect = (loc: location): void => {
    setNewLocation(loc)
  }

  const homelessChanged = (value: boolean): void => {
    // a homeless address has no street, and unchecking starts the street over empty
    setNewLocation({ ...newLocation, isHomeless: value, address: '', apartment: '' })
  }

  useEffect(() => {
    if (props.showDistrict !== true) return
    const { address, city, state, zipCode } = newLocation
    const filled = (value: string | undefined): boolean => (value ?? '').trim() !== ''
    // a homeless address has no street to look up, so its district is always picked by hand
    if (isHomeless || !(filled(address) && filled(city) && filled(state) && filled(zipCode))) {
      setDistrict(null)
      setDistrictResolved(null)
      return
    }
    let cancelled = false
    // debounced so typing doesn't fire a lookup per keystroke
    const timer = setTimeout(() => {
      const lookup = async (): Promise<void> => {
        const query = new URLSearchParams({ address, city, state, zipCode })
        const { response, success } = await sendGet(`/CaregiverPortal/DistrictForAddress?${query.toString()}`, {})
        if (cancelled) return
        const resolved = success && response?.districtName != null && response.districtName !== ''
        setDistrict(resolved ? { districtName: response.districtName, isdName: response.isdName } : null)
        setDistrictResolved(resolved)
      }
      void lookup()
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [props.showDistrict, isHomeless, newLocation.address, newLocation.city, newLocation.state, newLocation.zipCode])

  useEffect(() => {
    if (!showManualDistrict || districtOptions !== null) return
    const loadDistricts = async (): Promise<void> => {
      const { response, success } = await sendGet(`/CaregiverPortal/DistrictsForISD?isdID=${props.isdID ?? 0}`, {})
      const options: lookup[] = success && response != null ? response : []
      setDistrictOptions(options)
      // an address's stored district may belong to another ISD - don't preselect one that isn't offered
      setManualDistrictID(prev => options.some(o => o.id === prev) ? prev : undefined)
    }
    void loadDistricts()
  }, [showManualDistrict])

  const isSubjectMember = (m: Member): boolean => props.subjectId != null && m.type === MemberType.Child && m.id === props.subjectId

  const isSelected = (m: Member): boolean => isSubjectMember(m) || selectedMembers.some(l => l.id === m.id && l.type === m.type)

  const members: Member[] = [
    ...(props.subjectId != null ? [{ id: props.subjectId, type: MemberType.Child, name: subjectLabel }] : []),
    ...(props.sharedChildren ?? []).map(c => ({ id: c.id, type: MemberType.Child, name: c.name }))
  ]

  const otherMembers = members.filter(m => !isSubjectMember(m))

  const handleTypeChange = (event: any): void => {
    const value = Number(event.target.value) as AddressChangeType
    setChangeType(value)
    if (value === AddressChangeType.Household) {
      setSelectedMembers(otherMembers.map(m => ({ id: m.id, type: m.type })))
    } else if (value === AddressChangeType.SinglePerson) {
      setSelectedMembers([])
    }
  }

  const handleSubmit = (): void => {
    const validationErrors: string[] = []
    if (!isHomeless && (newLocation.address == null || newLocation.address.trim() === '')) validationErrors.push('Address')
    if (newLocation.city == null || newLocation.city.trim() === '') validationErrors.push('City')
    if (newLocation.state == null || newLocation.state.trim() === '') validationErrors.push('State')
    if (newLocation.zipCode == null || newLocation.zipCode.trim() === '') validationErrors.push('ZipCode')
    // required whenever the picker is shown - unless the child's ISD has no districts to pick from
    if (showManualDistrict && districtOptions?.length !== 0 && manualDistrictID === undefined) validationErrors.push('District')

    if (validationErrors.length > 0) {
      setFieldErrors(validationErrors)
      return
    }
    setFieldErrors([])

    let finalSelectedMembers: Array<{ id: number, type: MemberType }> = []
    if (!hideSharedChoice) {
      finalSelectedMembers = members.filter(m => isSelected(m)).map(m => ({ id: m.id, type: m.type }))
      if (finalSelectedMembers.length === 0) {
        toast.error('Please select at least one member')
        return
      }
    }

    // a hand-picked district is only sent while the picker is shown, so a resolved address keeps its looked-up district
    props.onSubmit({ ...newLocation, districtID: showManualDistrict ? manualDistrictID : undefined }, changeType, finalSelectedMembers)
  }

  const memberChange = (id: number, type: MemberType, adding: boolean): void => {
    setChangeType(AddressChangeType.SelectMembers)
    const existing = selectedMembers.find(l => l.id === id && l.type === type)
    if (adding && existing === undefined) {
      setSelectedMembers([...selectedMembers, { id, type }])
    } else if (!adding && existing !== undefined) {
      setSelectedMembers(selectedMembers.filter(l => l.id !== id || l.type !== type))
    }
  }

  const content =
    <>
      <CheckBoxWithLabel
        label='Currently Experiencing Homelessness'
        dataTestId='homeless'
        name='isHomeless'
        value={isHomeless}
        setValue={homelessChanged}
        edge='start'
      />
      {!hideSharedChoice && members.length > 0 && (
        <>
          <RadioGroup value={changeType} onChange={handleTypeChange}>
            <FormControlLabel value={AddressChangeType.Household} control={<Radio />} label='All Members' />
            <FormControlLabel value={AddressChangeType.SinglePerson} control={<Radio />} label={`Just ${subjectLabel}`} />
            <FormControlLabel value={AddressChangeType.SelectMembers} control={<Radio />} label='Select Manually' />
          </RadioGroup>
          {members.map(m => {
            const isSubject = isSubjectMember(m)
            return <CheckBoxWithLabel
              key={`${m.type}${m.id}`}
              label={m.name}
              value={isSelected(m)}
              name={`member${m.type}${m.id}`}
              setValue={(value) => { if (!isSubject) memberChange(m.id, m.type, value) }}
              edge={'start'}
            />
          })}
        </>
      )}

      <Box sx={{ '& .MuiOutlinedInput-root, & .MuiFilledInput-root': { backgroundColor: 'white' } }}>
        <LocationSelect hidePrimary={true} showHomelessOption={false} hideStreet={isHomeless} onEnter={locationSelect} location={newLocation} errors={fieldErrors} />
      </Box>

      {props.showDistrict === true && district != null && (
        <Box data-testid='addressDistrict' sx={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
          <SchoolIcon size={24} />
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#2C2C2C', lineHeight: 1.3 }}>{district.districtName}</Typography>
            <Typography sx={{ fontSize: '14px', color: '#2C2C2C', lineHeight: 1.3, fontStyle: 'italic' }}>{district.isdName}</Typography>
          </Box>
        </Box>
      )}

      {showManualDistrict && districtOptions !== null && (
        <Box sx={{ '& .MuiOutlinedInput-root, & .MuiFilledInput-root': { backgroundColor: 'white' } }} data-testid='manualDistrict'>
          <SelectWithLabel
            name='districtID'
            label='School District'
            options={districtOptions}
            value={manualDistrictID}
            onChange={(e) => { setManualDistrictID(Number(e.target.value)) }}
            width='100%'
            required={true}
            error={fieldErrors.includes('District')}
            showRequiredText={fieldErrors.includes('District')}
          />
        </Box>
      )}
    </>

  if (props.inline === true) {
    const buttonSx = { borderRadius: '24px', height: '52px', textTransform: 'none' as const, fontWeight: 600 }
    return (
      <Box sx={{ backgroundColor: '#ECEDEF', borderRadius: '10px', p: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {props.title != null && <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#222024' }}>{props.title}</Typography>}
        {content}
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Button data-testid='cancelButton' variant='outlined' color='inherit' fullWidth onClick={handleClose} sx={buttonSx}>
            Cancel
          </Button>
          <Button data-testid='updateButton' variant='contained' color='secondary' fullWidth onClick={handleSubmit} sx={buttonSx}>
            Update
          </Button>
        </Box>
      </Box>
    )
  }

  return <Modal
    open={props.open}
    titleContent={<>{props.title ?? 'Change Address'}</>}
    confirmationContent={content}
    onClose={handleClose}
    onConfirm={handleSubmit}
  />
}
